import React from "react";
import { Link } from "react-router";
import { useParams } from "react-router";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import Button from '../../../shared/ui/Button';
import Modal from '../../../shared/ui/Modal';
import Tabs, { Tab } from '../../../shared/ui/Tabs';
import { useAuth } from "../../../features/auth/AuthContext";
import {
    fetchItemGroupFn,
    updateItemGroupFn,
    createItemGroupFn,
    deleteItemGroupFn
} from "../item-groups.api";
import { isPayloadError } from "../../../shared/api/helpers";
import {
    ItemGroupInputs,
    ItemGroupInputsDefault
} from '../model/item-group.types';

import ItemGroupBasicFields from "../components/ItemGroupBasicFields";
import ItemGroupScheduleForm from "../components/ItemGroupScheduleForm";

interface ItemGroupEditorProps {
    groupId?: string;
    onClose?: () => void;
    onChanged?: () => void;
}

const ItemGroupEditor: React.FC<ItemGroupEditorProps> = ({ groupId, onClose, onChanged }) => {

    const auth = useAuth();
    const authRef = React.useRef(auth);
    const params = useParams();
    const navigate = useNavigate();
    const currentGroupId = groupId ?? params.id;
    const isModal = Boolean(onClose);
    const completeChange = () => onChanged ? onChanged() : navigate("/itemgroups/");

    const [item, setItem] = React.useState<any>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [showDeleteModal, setshowDeleteModal] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [processMessage, setProcessMessage] = React.useState<string | null>(null);

    const [activeTab, setActiveTab] = React.useState<'basic' | 'schedule'>('basic');

    React.useEffect(() => {
        authRef.current = auth;
    }, [auth]);

    // reactHookForm
    const {
        register,
        handleSubmit,
        setValue,
        setError, formState: { errors }
    } = useForm<ItemGroupInputs>({ defaultValues: ItemGroupInputsDefault });

    // useEffect: 데이터 로드: itemGroup
    React.useEffect(() => {
        if (!currentGroupId) return;

        setIsLoading(true);
        fetchItemGroupFn(currentGroupId)
            .then((data) => {
                setItem(data);
                setValue("name", data.name);
                setValue("description", data.description);
                setValue("sort_order", data.sort_order);
            })
            .catch((error) => {
                if (error.response?.status === 401) {
                    authRef.current.logout();
                    return;
                }
                setErrorMessage("데이터 로드에 실패했습니다");
            })
            .finally(() => setIsLoading(false));
    }, [currentGroupId, setValue])

    const onSubmit = async (data: ItemGroupInputs) => {
        setErrorMessage(null);
        setProcessMessage("처리 중입니다...");

        const payload = {
            name: data.name,
            description: data.description,
            sort_order: data.sort_order
        }

        try {
            if (currentGroupId && item) {
                await updateItemGroupFn({ uid: currentGroupId, payload });
            } else if (!currentGroupId) {
                await createItemGroupFn(payload);
            }
            completeChange();
        } catch (error: any) {
            if (error.response?.status === 401) {
                auth?.logout();
                return;
            }
            const errorData = error.response?.data;
            if (isPayloadError(errorData, {})) {
                for (const [key, value] of Object.entries(errorData)) {
                    setError(key as any, { type: "custom", message: value as string });
                }
            } else {
                setErrorMessage("시스템 오류입니다. 잠시 후 다시 시도하세요");
            }
        } finally {
            setProcessMessage(null);
        }
    }

    const handleModalDeleteOpen = () => setshowDeleteModal(true)
    const handleModalDeleteClose = () => setshowDeleteModal(false)
    const handleDelete = async () => {
        if (!currentGroupId) return;

        setProcessMessage("삭제 중입니다...");
        try {
            await deleteItemGroupFn(currentGroupId);
            completeChange();
        } catch (error: any) {
            if (error.response?.status === 401) {
                auth?.logout();
                return;
            }
            let errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
            try {
                const errorData = error.response?.data;
                if (errorData && errorData.errors && errorData.errors.length > 0) {
                    errorMessage = errorData.errors[0];
                }
            } catch (parseError) {
                // pass
            }
            setErrorMessage(errorMessage);
        } finally {
            setshowDeleteModal(false);
            setProcessMessage(null);
        }
    }

    const content = (<>
        {errorMessage && (
            <div className="alert alert-danger" role="alert">
                {errorMessage}
            </div>
        )}
        {processMessage && (
            <div className="alert alert-info" role="alert">
                {processMessage}
            </div>
        )}
        <div className={isModal ? "d-flex justify-content-end mb-3" : "page-head d-flex justify-content-between align-items-center mb-0"}>
            {!isModal && <div className="pt-3">
                <p className="fs-5">
                    상품그룹
                </p>
            </div>}
            <div>
                {item && (
                    <>
                        <button type="button" className="btn btn-danger" onClick={handleModalDeleteOpen}>
                            삭제
                        </button>
                    </>
                )}
                {!isModal && <Link to="/itemgroups/" className="btn btn-outline-secondary ms-2">뒤로가기</Link>}
            </div>
        </div>

        {!isModal && <hr className="my-0 mb-3" />}

        <form className="form-card" onSubmit={handleSubmit(onSubmit)}>

            <Tabs
                activeKey={activeTab}
                onSelect={(key) => key && setActiveTab(key as typeof activeTab)}
                ariaLabel="상품 그룹 설정"
            >
                <Tab eventKey="basic" title="기본 설정">
                    <ItemGroupBasicFields
                        auth={auth}
                        register={register}
                        errors={errors}
                    />
                </Tab>
                <Tab eventKey="schedule" title="스케줄 설정" disabled={!item}>
                    {item && (
                        <ItemGroupScheduleForm
                            auth={auth}
                            itemGroupId={currentGroupId || ''}
                        />
                    )}
                </Tab>
            </Tabs>

            <div className="my-2 d-flex gap-2">
                <button type="submit" className="btn btn-primary">
                    {item ? '변경' : '등록'}
                </button>
                {isModal
                    ? <button type="button" className="btn btn-outline-secondary" onClick={onClose}>취소</button>
                    : <Link to="/items/" className="btn btn-outline-secondary">취소</Link>}
            </div>

        </form>

        <Modal show={showDeleteModal} onHide={handleModalDeleteClose}>
            <Modal.Header closeButton>
                <Modal.Title>확인</Modal.Title>
            </Modal.Header>
            <Modal.Body>삭제하시겠습니까?</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleModalDeleteClose}>취소</Button>
                <Button variant="danger" onClick={handleDelete} disabled={isLoading}>삭제</Button>
            </Modal.Footer>
        </Modal>

    </>);

    if (!isModal) return content;

    return (
        <Modal show onHide={onClose!} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>상품그룹 변경</Modal.Title>
            </Modal.Header>
            <Modal.Body className="item-editor-modal-body">{content}</Modal.Body>
        </Modal>
    );
}


export default ItemGroupEditor;
